import {useEffect, useRef, useState} from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { CreateDishImage, GetDishById, UpdateDish } from '../../../../../reduxToolkit/FoodieDishes/DishesApi';
import { TABLES } from '../../../../../Apis/Tables';
import defaultImage from '../../../../../assets/IconsButtons/ic-ingredient-card.svg';
import {getPhotoPicker, PHOTO_PICKER_MODEL} from '../../../../../components/PhotoPicker/SubComponent/Models/Models';
import { DeleteItemsApi } from '../../../../../reduxToolkit/Commons/CommonsApi';
import { toast } from 'react-toastify';
import { UploadImage } from '../../../../../reduxToolkit/ShareSlice/shareApi';
import { DISH_MODEL } from './../Model/Models';
import HandleChange from './../../../../../Helpers/HandleChange';
import { Allergens } from '../../../../../enums/FoodieIcons';
import {
    DeleteAllergenById,
    DeleteDietTypeById,
    DeleteIngredientById,
} from '../../../../../reduxToolkit/FoodieIngredient/IngredientApi';
import { errorMsg } from '../../../../../Apis/functions';
import { permissionObj } from './../../../../../Helpers/permission';
import {getUnitTransByName} from "../../Ingredients/Model/Models";

/**
 * @Author: Susana Garcia Rodriguez
 * @JobTitle : Frontend
 * @Email : sgarcia@ibl.mx
 *
 */
const ManageDishApp = () => {
    const { id } = useParams();

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [item, setItem] = useState({ ...DISH_MODEL });
    const { handleChange } = HandleChange({ model: item, setModel: setItem });
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    const permissionUpdate = (DEV || permission?.includes(permissionObj?.WEB_FOODIE_ITEM_UPDATE));
    const { dishById } = useSelector((state) => state.DishSlice);
    const [disabled, setDisabled] = useState(!permissionUpdate);
    const [selectedIngredients, setSelectedIngredients] = useState([]);
    const [selectedAllergens, setSelectedAllergens] = useState([]);
    const [selectedDietTypes, setSelectedDietTypes] = useState([]);
    const [emptyFields, setEmptyFields] = useState(false);
    const [errorCode, setErrorCode] = useState(true);
    const [errorName, setErrorName] = useState(true);
    const [photoPicker, setPhotoPicker] = useState({ ...PHOTO_PICKER_MODEL, preview: defaultImage });
    const [imageId, setImageId] = useState(null);
    const [emptyIngredients, setEmptyIngredients] = useState(false);
    const [emptyAllergens, setEmptyAllergens] = useState(false);
    const [modified, setModified] = useState(false);
    const prevPhotoRef = useRef({
        fileSelected: null,
        selfieSrc: null,
    });

    // Fetch dish by ID on mount
    useEffect(() => {
        if (id) {
            getData();
        }
    }, [id]);

    useEffect(() => {
        if (modified) {
            getData();
            setModified(false);
        }
    }, [modified]);

    const getData = () => {
        dispatch(GetDishById(id)).then(
            ({
                 payload: {
                     data: { data },
                 },
             }) => {
                if (data?.id) {
                    setItem(DISH_MODEL.set(data));
                    setSelectedIngredients(
                        data?.ingredients?.map((e) => ({
                            id: e.ingredientId,
                            label: e.ingredientName,
                            subtitle: t(getUnitTransByName(e.unit)),
                            unit: e.quantity,
                        })),
                    );
                    setSelectedAllergens(
                        data?.allergenIds?.map((e) => ({
                            id: e,
                            label: t(Allergens[e]?.name || ''),
                        })),
                    );
                    setSelectedDietTypes(
                        data?.dietTypeIds?.map((e) => ({
                            id: e,
                        })),
                    );

                    setImageId(data.imageId || null);
                    setPhotoPicker((prev) => ({
                        ...prev,
                        previewImage: data.image ? `data:image/png;base64,${data.image}` : null,
                        id: data.imageId || null
                    }));
                }
            },
        );
    }

    useEffect(() => {
        setEmptyIngredients(!(Array.isArray(selectedIngredients) && selectedIngredients.length > 0));
    }, [selectedIngredients]);

    useEffect(() => {
        setEmptyAllergens(!(Array.isArray(selectedAllergens) && selectedAllergens.length > 0));
    }, [selectedAllergens]);

    // Handle update logic with validation and image upload
    const handleUpdate = () => {
        if (
            !item.isValidSetUpData() ||
            selectedIngredients?.length === 0 ||
            !Array.isArray(selectedAllergens) ||
            selectedAllergens.length === 0
        ) {
            setEmptyFields(true);
            toast.warn(t('please_fill_all_fields'));
            return;
        }

        if (selectedIngredients?.length === 0) {
            setEmptyIngredients(true);
        } else {
            setEmptyIngredients(false);
        }

        if (!Array.isArray(selectedAllergens) || selectedAllergens.length === 0) {
            setEmptyAllergens(true);
        } else {
            setEmptyAllergens(false);
        }

        let aux = {
            ...item,
            ingredients: selectedIngredients.map((ingredient) => ({
                ingredientId: ingredient.ingredientId || ingredient.id,
                quantity: ingredient.unit,
            })),
            allergenIds: selectedAllergens.map((item) => item.id),
            dietTypeIds: (selectedDietTypes || []).map((item) => item.id),
        };
        let body = aux.toRegister();
        dispatch(UpdateDish(body)).then(
            ({
                payload: {
                    data: { data, success, code },
                },
            }) => {
                if (success === true) {
                    const fileChanged = prevPhotoRef.current.fileSelected !== photoPicker?.fileSelected;
                    const selfieChanged = prevPhotoRef.current.selfieSrc !== photoPicker?.selfieSrc;
                    const imageProfile = getPhotoPicker(photoPicker);

                    if (!imageId && (fileChanged || selfieChanged) && imageProfile) {
                        createDishImage({ id: data.id, file: imageProfile});
                    } else if (imageId && !photoPicker?.id && imageProfile) {
                        updateDishImage({ file: imageProfile});
                    } else if (imageId && !photoPicker?.id && !imageProfile) {
                        handleDeleteImage();
                    } else {
                        toast.success(t('has_been_updated_successfully'));
                        setModified(true);
                    }
                } else {
                    errorMsg(code);
                }
            },
        );
    };

    const createDishImage = ({ id, file }) => {
        dispatch(
            CreateDishImage({
                imgData: { foodieItemId: id },
                image: file,
            }),
        ).then(
            ({
                 payload: {
                     data: { success, code },
                 },
             }) => {
                if (success) {
                    toast.success(t('has_been_updated_successfully'));
                    setModified(true);
                    prevPhotoRef.current = {
                        fileSelected: photoPicker?.fileSelected,
                        selfieSrc: photoPicker?.selfieSrc,
                    };
                } else errorMsg(code);
            },
        );
    }

    const updateDishImage = ({ file }) => {
        let formData = new FormData();
        formData.append('id', imageId);
        formData.append('option', TABLES.DISH);
        formData.append('file', file);
        dispatch(UploadImage(formData)).then(
            ({
                 payload: {
                     data: { success, code },
                 },
             }) => {
                if (success) {
                    toast.success(t('has_been_updated_successfully'));
                    prevPhotoRef.current = {
                        fileSelected: photoPicker?.fileSelected,
                        selfieSrc: photoPicker?.selfieSrc,
                    };
                    setModified(true);
                } else errorMsg(code);
            },
        );
    }

    // Delete dish image
    const handleDeleteImage = () => {
        dispatch(DeleteItemsApi({
            tableName: TABLES.DISH_IMAGE,
            body: [dishById?.id],
            showToast: false
        })).then(
            ({
                 payload: {
                     data: { success },
                 },
             }) => {
                if (success === true) {
                    toast.success(t('has_been_updated_successfully'));
                    prevPhotoRef.current = {
                        fileSelected: photoPicker?.fileSelected,
                        selfieSrc: photoPicker?.selfieSrc,
                    };
                    clearImage();
                    setModified(true);
                }
            },
        );
    };

    // Handles deletion of an ingredient from the current dish
    const handleIngredientDelete = async (element) => {
        await dispatch(
            DeleteIngredientById({
                foodieItemId: id,
                ingredientId: element.id,
            }),
        ).then(
            ({
                payload: {
                    data: { success, code },
                },
            }) => {
                if (success) {
                    toast.success(t('has_been_deleted_successfully'));
                } else {
                    errorMsg(code);
                }
            },
        );
    };

    // Handles deletion of a diet type from the current dish
    const handleDietTypeDelete = async (element) => {
        await dispatch(
            DeleteDietTypeById({
                foodieItemId: id,
                dietTypeId: element.id,
            }),
        ).then(
            ({
                payload: {
                    data: { success, code },
                },
            }) => {
                if (success) {
                    toast.success(t('has_been_deleted_successfully'));
                } else {
                    errorMsg(code);
                }
            },
        );
    };

    // Handles deletion of an allergen from the current dish
    const handleAllergenDelete = async (element) => {
        await dispatch(
            DeleteAllergenById({
                foodieItemId: id,
                allergenId: element.id,
            }),
        ).then(
            ({
                payload: {
                    data: { success, code },
                },
            }) => {
                if (success) {
                    toast.success(t('has_been_deleted_successfully'));
                } else {
                    errorMsg(code);
                }
            },
        );
    };

    const clearImage = () => {
        setPhotoPicker({ ...PHOTO_PICKER_MODEL, preview: defaultImage, id: null });
    };

    return {
        t,
        DEV,
        permission,
        item,
        handleChange,
        disabled,
        emptyFields,
        errorCode,
        errorName,
        selectedIngredients,
        setSelectedIngredients,
        selectedAllergens,
        setSelectedAllergens,
        selectedDietTypes,
        setSelectedDietTypes,
        handleUpdate,
        handleDeleteImage: clearImage,
        photoPicker,
        setPhotoPicker,
        handleIngredientDelete,
        handleDietTypeDelete,
        handleAllergenDelete,
        emptyAllergens,
        emptyIngredients,
        permissionUpdate
    };
};

export default ManageDishApp;
